import React, { useEffect, useRef, useState } from "react";

// MD-K12, CMP Accelerator Member Dashboard (2) ACC:806-824, logic ACC:3293-3327:
// a date box that opens an inline calendar (Monday first, month and year selects,
// gold selected day). Values stay "YYYY-MM-DD", as with the date picker it replaces.

const GOLD = "#E1A731";
const TEXT = "#000000";
const GRAY_600 = "#6B6B6B";
const BORDER = "#9E9E9E";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const pad = (value: number) => String(value).padStart(2, "0");
const toIso = (year: number, month: number, day: number) => `${year}-${pad(month + 1)}-${pad(day)}`;
const parseIso = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

/** Design fmtDate: "Oct 18, 2026". */
export const formatCmpDate = (value: string) => {
    const date = parseIso(value);
    return date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

const selectStyle: React.CSSProperties = {
    fontFamily: "'DM Sans', sans-serif", fontSize: 14, border: `1px solid ${BORDER}`, borderRadius: 999, color: TEXT, outline: "none", background: "#FFFFFF",
};
const arrowStyle: React.CSSProperties = { fontSize: 18, color: TEXT, cursor: "pointer", padding: "0 4px", background: "none", border: "none", fontFamily: "inherit" };

export const CmpInlineDateField = ({ value, onChange, ariaLabel, placeholder, minDate }: {
    value: string;
    onChange: (value: string) => void;
    ariaLabel: string;
    placeholder: string;
    /** Earliest day that can be picked ("YYYY-MM-DD"). */
    minDate?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(0);
    const [year, setYear] = useState(2026);
    const box = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event: MouseEvent) => { if (box.current && !box.current.contains(event.target as Node)) setOpen(false); };
        const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", escape);
        return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", escape); };
    }, [open]);

    const toggle = () => {
        if (!open) {
            const base = parseIso(value) || new Date();
            setMonth(base.getMonth());
            setYear(base.getFullYear());
        }
        setOpen(!open);
    };
    const thisYear = new Date().getFullYear();
    const firstYear = Math.min(2024, year, thisYear);
    const lastYear = Math.max(2030, year, thisYear + 4);
    const years = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return (
        <div ref={box} style={{ position: "relative", width: "100%" }}>
            <button type="button" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} onClick={toggle}
                style={{ width: "100%", border: `1px solid ${TEXT}`, borderRadius: 10, padding: "13px 16px", boxSizing: "border-box", display: "flex",
                    alignItems: "center", gap: 12, cursor: "pointer", background: "#FFFFFF", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                <span style={{ fontSize: 14, color: TEXT, flex: 1 }}>{value ? formatCmpDate(value) : placeholder}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={TEXT} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>
            </button>
            {open && (
                <div role="dialog" aria-label={`${ariaLabel} calendar`} style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 60, background: "#FFFFFF",
                    border: "1px solid #ECECEE", borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,.16)", padding: 16, width: 330, maxWidth: "calc(100vw - 48px)",
                    boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button type="button" aria-label="Previous month" style={arrowStyle}
                            onClick={() => (month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1))}>‹</button>
                        <select aria-label="Month" value={month} onChange={(event) => setMonth(Number(event.target.value))}
                            style={{ ...selectStyle, flex: 1, minWidth: 0, padding: "8px 14px" }}>
                            {MONTHS.map((name, index) => <option key={name} value={index}>{name}</option>)}
                        </select>
                        <select aria-label="Year" value={year} onChange={(event) => setYear(Number(event.target.value))} style={{ ...selectStyle, padding: "8px 12px" }}>
                            {years.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                        <button type="button" aria-label="Next month" style={arrowStyle}
                            onClick={() => (month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1))}>›</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginTop: 14 }}>
                        {WEEKDAYS.map((day, index) => <div key={index} style={{ textAlign: "center", fontSize: 12, color: GRAY_600 }}>{day}</div>)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginTop: 6 }}>
                        {Array.from({ length: firstWeekday }, (_, index) => <div key={`blank-${index}`} style={{ height: 34 }} />)}
                        {Array.from({ length: daysInMonth }, (_, index) => {
                            const iso = toIso(year, month, index + 1);
                            const selected = iso === value;
                            const blocked = !!minDate && iso < minDate;
                            return (
                                <button key={iso} type="button" disabled={blocked} aria-label={formatCmpDate(iso)} aria-pressed={selected}
                                    onClick={() => { onChange(iso); setOpen(false); }}
                                    style={{ height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 9, fontSize: 13, border: "none",
                                        fontFamily: "inherit", background: selected ? GOLD : "transparent", color: selected ? "#FFFFFF" : blocked ? "#C6C6C6" : TEXT,
                                        cursor: blocked ? "not-allowed" : "pointer", fontWeight: selected ? 600 : 400, padding: 0 }}>{index + 1}</button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CmpInlineDateField;
